import { ICompileService } from "angular";
import { get, startCase } from "lodash";
import { CellDoubleClickedEventHandler } from "../cells";
import {
  booleanValueGetterFactory,
  dateTimeValueGetter,
  dateValueGetter,
  lookupValueGetterFactory,
  ValueGetter,
} from "../cells/valueGetters";
import { ColDef } from "./colDef";
import { FilterProvider } from "./filters/FilterProvider";
import { OptionsFilter } from "./filters/subFilters/OptionsFilter";

export const vsName = "columnDefinitionService";

export interface PermissionColDef {
  requiresPermission?: string | string[];
}

export interface optionColDef extends simpleColDef {
  options: { display: string; value: string }[];
}

export interface simpleColDef extends PermissionColDef {
  field: string;
  header?: string;
  valueGetter?: ValueGetter;
  suppressMenu?: boolean;
  suppressSorting?: boolean;
  hide?: boolean;
  filter?: Function;
  cellDoubleClicked?: CellDoubleClickedEventHandler;
  cellRenderer?: awk.grid.ICellRenderer;
}

export interface fileColDef extends PermissionColDef {
  field: string;
  header?: string;
  hide?: true;
}
export interface childFileColDef extends PermissionColDef {
  prefix: string;
  header?: string;
  hide?: true;
}

export interface commandColDef extends PermissionColDef {
  headerName: string;
  name: string;
  iconClass: string;
  width?: number;
  colId?: string;
  requiresPermission?: string;
  hide?: boolean;
}

export interface selectColDef extends PermissionColDef {
  headerName?: string;
}

export interface linkColDef extends PermissionColDef {
  width?: number;
  colId: string;
  iconClass: string;
  stateExpression: string;
  headerName: string;
}

export interface AddInfoEditColumnsConfig {
  editPermission?: string | string[];
  viewPermission?: string | string[];
  infoSref?: string;
  editSref?: string;
  paramExpression?: string;
}

export class ColumnDefinitionService {
  static $inject = ["$compile"];
  constructor(private readonly $compile: ICompileService) {}

  addInfoEditColumns(
    columns: ColDef[] | undefined,
    config?: AddInfoEditColumnsConfig
  ): ColDef[] {
    const infoSref = config?.infoSref ?? ".info.redirect";
    const editSref = config?.editSref ?? ".info.edit";
    const paramExpression = config?.paramExpression ?? "{ id: data.Id }";
    return [
      this.getLinkColDef({
        colId: "info",
        headerName: "info",
        iconClass: "fa fa-info-circle text-primary",
        stateExpression: `${infoSref}(${paramExpression})`,
        requiresPermission: config?.viewPermission,
      }),
      this.getLinkColDef({
        colId: "edit",
        headerName: "edit",
        iconClass: "fa fa-pencil",
        stateExpression: `${editSref}(${paramExpression})`,
        requiresPermission: config?.editPermission,
      }),
      ...(columns ?? []),
    ];
  }

  getSelectColDef(config?: selectColDef): ColDef {
    return {
      colId: "select",
      headerName: config?.headerName ?? "Select",
      width: 20,
      template: `
      <input
        type="checkbox"
        ng-click="recordGridController.toggleRowSelection(data)"
        ng-checked="recordGridController.rowSelected(data)"
      />`,
      suppressSorting: true,
      requiresPermission: config?.requiresPermission,
    };
  }

  getCommandColDef(config: commandColDef): ColDef {
    return {
      headerName: config.headerName,
      template: `<i ng-show="${config.name}" class="fa fa-lock text-danger" ng-click="$emit('command', {data, commandName:'${config.name}'})"></i>
      <i ng-show="!(${config.name})" class="${config.iconClass}" ng-click="$emit('command', {data, commandName:'${config.name}'})"></i>`,
      width: config.width ?? 20,
      colId: config.colId ?? config.headerName,
      requiresPermission: config.requiresPermission,
      hide: config.hide,
    };
  }

  getLinkColDef(config: linkColDef): ColDef {
    return {
      headerName: config.headerName,
      template: `<a ui-sref="${config.stateExpression}"><i class="${config.iconClass}"></i></a>`,
      colId: config.colId,
      width: config.width ?? 20,
      requiresPermission: config.requiresPermission,
    };
  }

  getBooleanColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultBooleanFilters()
          .getGridFilterFactory(),
      booleanValueGetterFactory()
    );
  }

  getIdColDef() {
    return this.simpleColDef({
      field: "Id",
      filter: new FilterProvider("Id", this.$compile)
        .defaultNumberFilters()
        .getGridFilterFactory(),
      header: "Id",
      hide: true,
    });
  }

  getIdentityColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultIdentityFilters()
          .getGridFilterFactory()
    );
  }

  getNumberColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultNumberFilters()
          .getGridFilterFactory()
    );
  }

  getStringColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultStringFilters()
          .getGridFilterFactory()
    );
  }

  getOptionColDef(config: optionColDef): ColDef {
    return this.simpleColDef(
      config,
      new FilterProvider("ChangeType", this.$compile)
        .addFilter(
          "=",
          new OptionsFilter({
            displayName: "=",
            queryFieldNames: [config.field],
            options: config.options,
          })
        )
        .getGridFilterFactory(),
      lookupValueGetterFactory({
        lookup: config.options.reduce<any>((result, option) => {
          result[option.value] = option.display;
          return result;
        }, {}),
      })
    );
  }

  getDateColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultDateFilters()
          .getGridFilterFactory(),
      dateValueGetter
    );
  }

  getDateTimeColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultDateFilters()
          .getGridFilterFactory(),
      dateTimeValueGetter
    );
  }

  getChildFileColDef(config: childFileColDef): ColDef {
    const fileNameField = config.prefix + "Name";
    const urlField = config.prefix + "Url";

    return {
      field: fileNameField,
      headerName: config.header ?? "File",
      hide: config.hide,
      requiresPermission: config.requiresPermission,
      valueGetter: (params: any) => get(params.data, fileNameField),
      cellRenderer: (params) => {
        const fileName = get(params.data, fileNameField);
        const url = get(params.data, urlField);
        if (!(fileName && url)) {
          return "";
        }
        return `<a href="${url}" target="_blank">${fileName}</a>`;
      },
    };
  }

  getFileColDef(config: fileColDef): ColDef {
    return {
      field: config.field,
      headerName: config.header ?? config.field,
      hide: config.hide,
      requiresPermission: config.requiresPermission,
      valueGetter: (params: any) => get(params.data, `${config.field}.Name`),
      cellRenderer: (params) => {
        const file = get(params.data, config.field);
        if (!file) {
          return "";
        }

        return `<a href="${file.Url}" target="_blank">${file.Name}</a>`;
      },
    };
  }

  simpleColDef(
    config: simpleColDef,
    filter?: Function,
    valueGetter?: ValueGetter
  ): ColDef {
    return {
      field: config.field,
      headerName: this.getHeader(config),
      filter,
      valueGetter: config.valueGetter ?? valueGetter,
      suppressMenu: config.suppressMenu,
      suppressSorting: config.suppressSorting,
      hide: config.hide,
      requiresPermission: config.requiresPermission,
      cellDoubleClicked: config.cellDoubleClicked,
      cellRenderer: config.cellRenderer,
    };
  }

  private getHeader(config: simpleColDef): string {
    return config.header ?? startCase(config.field);
  }
}
